/**
 * /join, /signin and /reset — create an account, sign in, or choose a new password after a
 * forgotten one, on a page of its own, without the feed.
 *
 * Mounted by src/index.tsx instead of App, like /stats: no Carousel, no TelemetryTransport, no
 * feed resource, no service worker, no window.__blitz. Signing up never starts a card session
 * on card 0, and every account door — the landing's, the catalogue's, and the feed's own (top
 * bar, + sheet, comments) — leads here instead of opening a sheet over the feed. The two forms
 * are one component: /join opens on create, /signin on sign in, and switching between them
 * moves the address with it, so each is a page that can be linked. This file
 * imports nothing that can emit a row (not App, the telemetry modules, api/ui, lib/share or
 * lib/visit), and tests/unit/join.test.ts pins that list.
 *
 * Who is here is PEEKED on load, never minted: opening the page does not make a stranger an auth
 * user and an actor row. Only Create account asks for a session, because an account is an
 * anonymous session upgraded in place (src/api/account.ts), so that is the moment one has to
 * exist. Sign-out goes through the peeking API too, and both reset the minting resolver so a
 * later create does not reuse the session that was signed out.
 *
 * The views and their words follow the account sheet's (src/components/AccountSheet.tsx), which
 * stays the door inside the feed; this page adds what a page needs and a sheet does not: its
 * own heading, and the way on into the feed once there is an account. The confirmation email
 * still links to the site root, which forwards to /feed signed in (docs/ACCOUNTS.md).
 */
import { Match, Show, Switch, createEffect, createSignal, on, onMount } from 'solid-js';
import { ANONYMOUS, account as mint, createAccountApi, validateEmail, validateHandle, validatePassword, type Account, type AccountClient } from '../api/account';
import { resetSession, supabase } from '../backend/supabase';
import { PAGE_FOR, feedDoor, joinViewFor, type JoinView } from '../lib/join';

/** The session as it is, never minted. */
const peekSession = async () => (supabase ? (await supabase.auth.getSession()).data.session : null);
const peek = createAccountApi(supabase as unknown as AccountClient | null, peekSession, resetSession);

const TITLES: Record<JoinView, string> = {
  create: 'Create your account',
  signin: 'Sign in',
  forgot: 'Reset your password',
  reset: 'Choose a new password',
  confirm: 'Check your inbox',
  handle: 'Pick your handle',
  done: 'Your account',
};

export function JoinPage() {
  const search = location.search;
  const why = new URLSearchParams(search).get('why');
  const [acct, setAcct] = createSignal<Account>(ANONYMOUS);
  const [view, setView] = createSignal<JoinView | null>(null);
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [handle, setHandle] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [note, setNote] = createSignal<string | null>(null);
  // A reset link that failed comes back with the reason in the fragment (expired, already used).
  const [linkBroken, setLinkBroken] = createSignal(false);
  let first: HTMLInputElement | undefined;

  onMount(async () => {
    const hash = new URLSearchParams(location.hash.slice(1));
    const a = await peek.load(); // supabase-js has read a reset link's session from the fragment by now
    setAcct(a);
    const v = joinViewFor(a, search, location.pathname);
    setLinkBroken(v === 'forgot' && (location.pathname.replace(/\/+$/, '') === '/reset' || hash.has('error')));
    // Whatever the link carried, tokens or an error must not sit in the address bar.
    if (location.hash) {
      try {
        history.replaceState(history.state, '', location.pathname + location.search);
      } catch {
        /* history unavailable */
      }
    }
    setView(v);
  });

  // A new view starts clean, focused, and names itself in the tab; the two forms are two pages,
  // so the address follows them (the old ?signin spelling is dropped as it is honoured).
  createEffect(
    on(view, (v) => {
      if (!v) return;
      setError(null);
      document.title = `${TITLES[v]} — Blitz`;
      if (v === 'create' || v === 'signin') {
        const q = new URLSearchParams(location.search);
        q.delete('signin');
        const next = PAGE_FOR[v] + (q.toString() ? `?${q}` : '');
        try {
          if (location.pathname + location.search !== next) history.replaceState(history.state, '', next);
        } catch {
          /* history unavailable: the form still works */
        }
      }
      queueMicrotask(() => first?.focus());
    }),
  );

  const precheck = (fields: Array<{ error: string } | Record<string, string>>): boolean => {
    for (const f of fields) {
      if ('error' in f) {
        setError(f.error);
        return false;
      }
    }
    return true;
  };

  const run = async (fn: () => Promise<void>) => {
    if (busy()) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const create = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateEmail(email()), validatePassword(password()), validateHandle(handle())])) return;
    void run(async () => {
      const r = await mint.create({ email: email(), password: password(), handle: handle() });
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setAcct(r.account);
      setPassword('');
      if (r.needsConfirmation) setView('confirm');
      else if (r.handleProblem) {
        // The account is real; only the name was refused. Say so, and ask for another.
        setHandle('');
        setView('handle');
        queueMicrotask(() => setError(r.handleProblem ?? null));
      } else setView('done');
    });
  };

  const signIn = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateEmail(email())])) return;
    void run(async () => {
      const r = await mint.signIn({ email: email(), password: password() });
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setAcct(r.account);
      setPassword('');
      setView(joinViewFor(r.account, search, location.pathname));
    });
  };

  const forgot = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateEmail(email())])) return;
    void run(async () => {
      const r = await peek.requestReset(email(), `${location.origin}/reset`);
      if (!r.ok) setError(r.reason);
      else {
        setLinkBroken(false);
        setNote(`If ${email().trim()} has an account, a link to choose a new password is on its way. Check spam too; it can take a minute.`);
      }
    });
  };

  const newPassword = (e: Event) => {
    e.preventDefault();
    if (!precheck([validatePassword(password())])) return;
    void run(async () => {
      const r = await peek.setPassword(password());
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setAcct(r.account);
      setPassword('');
      setView(r.account.handle ? 'done' : 'handle');
      setNote('Password changed. It works on every device from now on.');
    });
  };

  const confirmed = () =>
    void run(async () => {
      const a = await mint.refresh(); // a fresh token, so a confirmation done elsewhere shows up now
      setAcct(a);
      if (a.isAccount) setView(a.handle ? 'done' : 'handle');
      else setError('Not confirmed yet — the link is in your inbox. It can take a minute to arrive.');
    });

  const resend = () =>
    void run(async () => {
      const r = await mint.resend();
      if (!r.ok) setError(r.reason);
      else setNote('Sent again. Check spam too.');
    });

  const saveHandle = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateHandle(handle())])) return;
    void run(async () => {
      const r = await mint.claim(handle());
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      setAcct({ ...acct(), handle: r.handle });
      setView('done');
    });
  };

  const signOut = () =>
    void run(async () => {
      setAcct(await peek.signOut());
      setEmail('');
      setView('signin');
    });

  return (
    <div class="app">
      <header class="top-bar">
        {/* The mark is the way to the landing page (the site root) — the one place that explains what this is. */}
        <a class="brand" data-ui href="/" data-testid="brand-link" aria-label="About Blitz">
          <img src="/icons/icon.svg" alt="" />
          BLITZ
        </a>
        <div class="top-actions">
          <a class="btn" href={feedDoor(search)} data-testid="join-feed-top">
            Open the feed
          </a>
        </div>
      </header>

      <main class="join-page" data-testid="join-page" data-view={view() ?? 'loading'}>
        <section class="link-sheet join-card">
          <Show when={view()} fallback={<p class="hint">Reading who this is…</p>}>
            <Switch>
              <Match when={view() === 'create'}>
                {/* novalidate: the browser's bubble would pre-empt our own sentences. */}
                <form onSubmit={create} novalidate>
                  <h1>Create your account</h1>
                  <p class="hint">
                    Blitz is the feed for what people make with AI. An account puts what you make — an image, a clip, a page, a game — in the feed under your own handle. Looking around never needs one.
                  </p>
                  <label>
                    <span>Email</span>
                    <input ref={first} type="email" inputmode="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="acct-email" />
                  </label>
                  <label>
                    <span>Password</span>
                    <input type="password" autocomplete="new-password" placeholder="8 characters or more" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} data-testid="acct-password" />
                  </label>
                  <label>
                    <span>Handle</span>
                    <div class="at">
                      <b>@</b>
                      <input type="text" autocomplete="nickname" autocapitalize="off" spellcheck={false} placeholder="yourname" value={handle()} onInput={(e) => setHandle(e.currentTarget.value)} data-testid="acct-handle" />
                    </div>
                  </label>
                  <p class="hint">Your handle is the name on everything you add: lowercase letters, digits and underscores.</p>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <div class="row">
                    <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                      {busy() ? 'Creating…' : 'Create account'}
                    </button>
                  </div>
                  <p class="hint links">
                    Already have one?{' '}
                    <button type="button" class="linkish" onClick={() => setView('signin')} data-testid="acct-to-signin">
                      Sign in
                    </button>
                  </p>
                </form>
              </Match>

              <Match when={view() === 'signin'}>
                <form onSubmit={signIn} novalidate>
                  <h1>Sign in</h1>
                  <p class="hint">Your handle and what you added, on this device too.</p>
                  <Show when={why === 'link'}>
                    <p class="error" role="alert" data-testid="acct-why">That link did not work; it may have expired. Sign in, or create the account again.</p>
                  </Show>
                  <label>
                    <span>Email</span>
                    <input ref={first} type="email" inputmode="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="acct-email" />
                  </label>
                  <label>
                    <span>Password</span>
                    <input type="password" autocomplete="current-password" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} data-testid="acct-password" />
                  </label>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <div class="row">
                    <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                      {busy() ? 'Signing in…' : 'Sign in'}
                    </button>
                  </div>
                  <p class="hint links">
                    <button type="button" class="linkish" onClick={() => setView('forgot')} data-testid="acct-to-forgot">
                      Forgot your password?
                    </button>
                    {' · '}
                    New here?{' '}
                    <button type="button" class="linkish" onClick={() => setView('create')} data-testid="acct-to-create">
                      Create an account
                    </button>
                  </p>
                </form>
              </Match>

              <Match when={view() === 'forgot'}>
                <form onSubmit={forgot} novalidate>
                  <h1>Reset your password</h1>
                  <p class="hint">We will email you a link. Open it on this device and choose a new password; your handle and everything you added stay as they are.</p>
                  <Show when={linkBroken()}>
                    <p class="error" role="alert" data-testid="acct-link-broken">That link did not work; it may have expired or already been used. Send yourself a new one.</p>
                  </Show>
                  <label>
                    <span>Email</span>
                    <input ref={first} type="email" inputmode="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="acct-email" />
                  </label>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <Show when={note()}>
                    <p class="hint" role="status" data-testid="acct-note">{note()}</p>
                  </Show>
                  <div class="row">
                    <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                      {busy() ? 'Sending…' : 'Send the link'}
                    </button>
                  </div>
                  <p class="hint links">
                    <button type="button" class="linkish" onClick={() => setView('signin')} data-testid="acct-forgot-to-signin">
                      Back to sign in
                    </button>
                  </p>
                </form>
              </Match>

              <Match when={view() === 'reset'}>
                <form onSubmit={newPassword} novalidate>
                  <h1>Choose a new password</h1>
                  <p class="hint">
                    For <b>{acct().email ?? 'your account'}</b>. You are signed in on this device; the new password is how you sign in anywhere else.
                  </p>
                  <label>
                    <span>New password</span>
                    <input ref={first} type="password" autocomplete="new-password" placeholder="8 characters or more" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} data-testid="acct-password" />
                  </label>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <div class="row">
                    <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                      {busy() ? 'Saving…' : 'Save password'}
                    </button>
                  </div>
                </form>
              </Match>

              <Match when={view() === 'confirm'}>
                <div class="join-body">
                  <h1>Check your inbox</h1>
                  <p class="hint">
                    We sent a link to <b>{acct().pendingEmail ?? 'your email'}</b>. Open it to confirm your account; it brings you into the feed, signed in. Until then you can look around, but adding waits for the confirmed account.
                  </p>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <Show when={note()}>
                    <p class="hint" role="status" data-testid="acct-note">{note()}</p>
                  </Show>
                  <div class="row">
                    <button type="button" class="btn primary" disabled={busy()} onClick={confirmed} data-testid="acct-confirmed">
                      {busy() ? 'Checking…' : 'I opened it'}
                    </button>
                  </div>
                  {/* Every way out of a mistyped or missing email, so this view is never a dead end. */}
                  <p class="hint links">
                    <button type="button" class="linkish" disabled={busy()} onClick={resend} data-testid="acct-resend">
                      Send it again
                    </button>
                    {' · '}
                    <button
                      type="button"
                      class="linkish"
                      onClick={() => {
                        setEmail(acct().pendingEmail ?? '');
                        setView('create');
                      }}
                      data-testid="acct-change-email"
                    >
                      Wrong address? Change it
                    </button>
                    {' · '}
                    <button type="button" class="linkish" onClick={() => setView('signin')} data-testid="acct-confirm-to-signin">
                      Sign in to another account
                    </button>
                  </p>
                </div>
              </Match>

              <Match when={view() === 'handle'}>
                <form onSubmit={saveHandle} novalidate>
                  <h1>Pick your handle</h1>
                  <p class="hint">Your account is ready. The handle is the name on everything you add.</p>
                  <label>
                    <span>Handle</span>
                    <div class="at">
                      <b>@</b>
                      <input ref={first} type="text" autocomplete="nickname" autocapitalize="off" spellcheck={false} placeholder="yourname" value={handle()} onInput={(e) => setHandle(e.currentTarget.value)} data-testid="acct-handle" />
                    </div>
                  </label>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <div class="row">
                    <button type="submit" class="btn primary" disabled={busy() || !handle().trim()} data-testid="acct-save-handle">
                      Claim handle
                    </button>
                  </div>
                </form>
              </Match>

              <Match when={view() === 'done'}>
                <div class="join-body" data-testid="join-done">
                  <h1>You're in, @{acct().handle}.</h1>
                  <Show when={note()}>
                    <p class="hint" role="status" data-testid="acct-note">{note()}</p>
                  </Show>
                  <p class="hint">
                    Add an image, a clip, a page or a game you made with AI and it goes in under your handle. It is next for you; everyone else sees it once it has been looked at on a phone — usually today.
                  </p>
                  <div class="row">
                    <a class="btn" href={feedDoor(search)} data-testid="join-open-feed">
                      Open the feed
                    </a>
                    <a class="btn primary" href={feedDoor(search, { link: '1' })} data-testid="join-add">
                      Add what you made
                    </a>
                  </div>
                  <p class="hint links">
                    <Show when={acct().email}>
                      {acct().email} ·{' '}
                    </Show>
                    <button type="button" class="linkish" disabled={busy()} onClick={signOut} data-testid="acct-signout">
                      Sign out
                    </button>
                  </p>
                </div>
              </Match>
            </Switch>
          </Show>
        </section>
      </main>
    </div>
  );
}
